import type { PortableTextBlock } from '@portabletext/types';
import type { BlogTileData } from './Blog';
import type { CustomForm } from './IForms';
import type { CloudinaryAsset, FAQ, SanityImage, USP } from './Sanity';

export interface BulkPricingPrice {
  minimumQuantity: number;
  maximumQuantity: number | null;
  price: number;
}
export interface BulkPricingPercent {
  minimumQuantity: number;
  maximumQuantity: number | null;
  percentOff: number;
}
export interface BulkPricingAdjustment {
  minimumQuantity: number;
  maximumQuantity: number | null;
  priceAdjustment: number;
}

// Bulk Pricing Type Guards
export function isBulkPricingPrice(
  price: BulkPricingPrice | BulkPricingPercent | BulkPricingAdjustment
): price is BulkPricingPrice {
  return (price as BulkPricingPrice).price !== undefined;
}
export function isBulkPricingPercent(
  price: BulkPricingPrice | BulkPricingPercent | BulkPricingAdjustment
): price is BulkPricingPercent {
  return (price as BulkPricingPercent).percentOff !== undefined;
}

export interface Pricing {
  price: {
    value: number;
  };
  salePrice: {
    value: number;
  } | null;
  basePrice: {
    value: number;
  } | null;
  retailPrice: {
    value: number;
  } | null;
  mapPrice: {
    value: number;
  } | null;
  bulkPricing: (
    | BulkPricingPrice
    | BulkPricingPercent
    | BulkPricingAdjustment
  )[];
  priceRange: {
    min: {
      value: number;
      currencyCode: string;
    };
    max: {
      value: number;
      currencyCode: string;
    };
  };
}

export interface VariantOption {
  displayName: string;
  label: string;
  optionId: number;
  valueId: number;
}

export interface Variant {
  availableFrom: string;
  availableFromBanter: string;
  availableFromLoggedIn: string;
  images: SanityImage[];
  options: VariantOption[];
  optionTags: string[];
  pricing: Pricing;
  productId: number;
  sku: string;
  variantId: number;
  flavourDescription: string | null;
  availableAsSample: boolean | null;
  sampleVariantId: number | null;
}

export interface BaseSanityVariant {
  _createdAt: string;
  _id: string;
  _rev: string;
  _type: string;
  _updatedAt: string;
  productId: number;
  sku: string;
  variantId: number;
  optionTags: string[];
  availableAsSample: boolean | null;
  sampleVariantId: number | null;
  scheduledDatetimeAll: string;
  scheduledDatetimeBanter: string;
  scheduledDatetimeLoggedIn: string;
  flavourDescription: string | null;
}
export interface SanityVariant extends BaseSanityVariant {
  options: {
    title: string;
    value: string;
  }[];
  images: SanityImage[];
}
export interface ProductOption {
  displayName: string;
  entityId: number;
  isRequired: boolean;
  checkedByDefault?: boolean;
  displayStyle?: string;
  values: {
    entityId: number;
    label: string;
    isDefault: boolean;
    hexColors?: string[];
    imageUrl?: string;
  }[];
}
export interface ProductDescription {
  _key: string;
  _type: string;
  contentDescription: string;
  contentBody?: (PortableTextBlock | FAQ | any)[];
  relatedBlogs?: BlogTileData[];
}

export interface SanityBaseProduct {
  averageRating: number;
  discontinued: boolean;
  images: SanityImage[];
  price: number;
  entityId: number;
  shortDescription: string;
  shortName: string;
  slug: string;
  totalReviews: number;
  name: string;
  variants: SanityVariant[];
}
export interface SanityProduct extends SanityBaseProduct {
  _createdAt: string;
  _id: string;
  _rev: string;
  _type: string;
  _updatedAt: string;
  availability?: string;
  categories: string[];
  cta: SanityImage[];
  description: {
    _key: string;
    _type: string;
    contentDescription: string;
    contentBody: PortableTextBlock[];
  }[];
  defaultVariantId: number;
  freeSample: boolean;
  imgUrl: string;
  isFeatured: boolean;
  minPurchaseQty: number;
  maxPurchaseQty: number;
  optionSet: {
    options: {
      displayName: string;
      displayType: {
        _key: string;
        _type: string;
        displayStyle: string;
        values: string[];
      }[];
      entityId?: number;
    }[];
  };
  packageDescription: string;
  pricePerServe: number | null;
  servingSize: number;
  shortTitle: string;
  sku: string;
  sortOrder: number;
  spritePosition: string;
  starRatings: {
    title: string;
    value: number;
  }[];
  suggestFlavours: boolean;
  tags: Array<{ title: string; parent: string }>;
  tgaStatement?: string;
  theme: string;
  title: string;
  toggles: {
    title: string;
    value: boolean;
  }[];
  totalSold: number;
  usp?: USP[];
  referenceProduct?: { entityId: number; name: string; shortName: string };
  tiktokMetaTitle?: string;
  tiktokMetaDescription?: string;
  reviewSummary?: string;
}

export interface SanityProductSample {
  slug: string;
  images: CloudinaryAsset[];
  product: SanityProduct;
  sample: SanityProduct;
}

export interface ProductSample {
  slug: string;
  images: CloudinaryAsset[];
  description: (PortableTextBlock | FAQ | any)[];
  product: Product;
  sample: Product;
}

export interface BaseProduct {
  addToCartMessage?: string;
  averageRating: number;
  categoryBulletPoints?: string[]; // only used for products in category pages, comes from sanity category query
  defaultVariantId: number;
  discontinued: boolean;
  entityId: number; // I think the name comes from graphQL BC. Their REST is just id
  images: (SanityImage | CloudinaryAsset)[];
  maxPurchaseQty: number; // default is 9999
  minPurchaseQty: number; // default is 1
  name: string;
  options: ProductOption[]; // straight from BC
  pricePerServe: number | null;
  pricing: Pricing;
  servingSize: number;
  shortDescription: string | string[];
  shortName: string | null;
  sku: string;
  slug: string;
  tags: Array<{ title: string; parent: string }>;
  tgaStatement?: string;
  totalReviews: number;
  variants: Variant[]; // come from sanity with options from BC
}

export const isSuggestFlavoursForm = (
  item: boolean | CustomForm
): item is CustomForm => typeof item !== 'boolean';

export const isFullProduct = (
  product: BaseProduct | Product
): product is Product => Object.hasOwn(product, 'description');

export interface Product extends BaseProduct {
  _id: string;
  availability?: string;
  cta: (SanityImage | CloudinaryAsset)[];
  mobileCta: CloudinaryAsset[];
  defaultImgUrl: string;
  description: ProductDescription[];
  disableBulkBuy: boolean;
  freeSampleAvailable: boolean;
  packageDescription: string;
  serverSideStock?: boolean;
  starRatings: {
    title: string;
    value: number;
  }[];
  suggestFlavours: boolean | CustomForm;
  theme: string;
  toggles: {
    title: string;
    value: boolean;
  }[];
  usp?: USP[];
  referenceProduct?: { entityId: number; name: string; shortName: string };
  tiktokMetaTitle?: string;
  tiktokMetaDescription?: string;
  reviewSummary?: string;
}

export interface OrderProduct extends BaseProduct {
  quantity: number;
  price: number;
  variantId: number;
  type: string;
  availability?: string;
}

export interface FlavourCampaignProduct extends BaseProduct {
  productCardImage: CloudinaryAsset;
  selectedVariant: Variant;
}

export interface S3LabResult {
  id: number;
  name: string;
  url: string;
  date: string;
  formattedDate: string;
  flavour: string;
  type: string;
  dateGroup?: string;
}

// TODO: remove in a few months
export interface ProductArchived extends BaseProduct {
  availability?: string;
  cta?: SanityImage[];
  defaultImgUrl: string;
  defaultVariantId: number;
  description: ProductDescription[];
  freeSampleAvailable: boolean;
  options: ProductOption[];
  packageDescription: string;
  servingSize: number;
  reviews: number;
  serverSideStock?: boolean;
  starRatings: {
    title: string;
    value: number;
  }[];
  theme: string;
  toggles: {
    title: string;
    value: boolean;
  }[];
  usp?: USP[];
  url: string;
  referenceProduct?: { entityId: number; name: string; shortName: string };
}

export function isArchivedProduct(
  product: Product | ProductArchived
): product is ProductArchived {
  return (product as ProductArchived).url !== undefined;
}

// used in Homepage Carousel
export interface CarouselItemReferencedProduct {
  id: string;
  productId: number;
  sku: string;
  name: string;
  defaultVariant: {
    variantId: number;
  };
  slug: string;
  type: string;
}

export interface CarouselItemReferencedProductVariant {
  productId: number;
  sku: string;
  variantId: number;
}
